import { Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { SnackbarNotificationComponent } from '../feature-modules/shared/components/snackbar-notification/snackbar-notification.component';

export interface AuthError {
  message: string;
  name?: string;
  code?: string;
  __type?: string;
}

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  readonly customerSupportNumber: string = '855-282-6353';

  constructor(private matSnackBar: MatSnackBar) {}

  displayInfo(infoMessage: string): MatSnackBarRef<any> {
    return this.matSnackBar.openFromComponent(SnackbarNotificationComponent, {
      duration: 10000,
      verticalPosition: 'top',
      horizontalPosition: 'center',
      panelClass: 'snackbar-notification--info',
      data: {
        notificationType: 'info',
        text: infoMessage,
      },
    });
  }

  /**
   * Useful when you need to pass innerHTML to Snackbar
   */
  displayCustomErrorSnackbar(data: string) {
    return this.matSnackBar.openFromComponent(SnackbarNotificationComponent, {
      duration: 10000,
      verticalPosition: 'top',
      horizontalPosition: 'center',
      panelClass: 'snackbar-notification--error',
      data: {
        notificationType: 'error',
        text: data,
      },
    });
  }

  displayServiceError(
    errorMessage: string = 'It’s not you. It’s us. Please give it another try.'
  ): MatSnackBarRef<any> {
    return this.matSnackBar.openFromComponent(SnackbarNotificationComponent, {
      duration: 10000,
      verticalPosition: 'top',
      horizontalPosition: 'center',
      panelClass: 'snackbar-notification--error',
      data: {
        notificationType: 'error',
        text: errorMessage,
      },
    });
  }

  /** The reason to create a custom nested error flow is because we would like to display aws errors
   * for some use cases (unconfirmed customers who try to reset password),
   * but fallback to our owen error messages or default for other errors.
   */
  displayClientError(error: AuthError): MatSnackBarRef<any> {
    const DEFAULT_ERROR = `Something went wrong. Please retry the request or contact customer support ${this.customerSupportNumber} for assistance`;
    let errorMessage: string;
    let nestedException: string;
    let nestedErrorMessage: string;

    const errorCode = error.name || error.code || error.__type;

    switch (errorCode) {
      // Register Flow Error
      case 'InvalidRegistrationException':
        try {
          nestedErrorMessage = error.message.toLowerCase();
        } catch (e) {
          nestedErrorMessage = DEFAULT_ERROR;
        }
        if (nestedErrorMessage.includes('no cid found')) {
          errorMessage = `Unable to find your account. Please verify your input.`;
        } else if (nestedErrorMessage.includes('already registered')) {
          errorMessage = `You already have a username registered.`;
        } else if (nestedErrorMessage.includes('multiple cid found')) {
          errorMessage = `Account data issue. Please contact customer support ${this.customerSupportNumber} for assistance.`;
        } else {
          errorMessage = `User Lookup failed. Please retry.`;
        }
        break;
      case 'UsernameExistsException':
        errorMessage = error.message;
        break;
      case 'UserNotFoundError':
        errorMessage = error.message;
        break;
      case 'UserNotFoundException':
        errorMessage = 'Username does not exist. Please verify your input or register your account';
        break;
      case 'NotAuthorizedException':
        try {
          nestedErrorMessage = error.message.toLowerCase();
        } catch (e) {
          nestedErrorMessage = DEFAULT_ERROR;
        }
        if (nestedErrorMessage.includes('user is disabled')) {
          errorMessage = `Account is suspended. Please contact customer support ${this.customerSupportNumber} for assistance.`;
        } else if (nestedErrorMessage.includes('incorrect username or password.')) {
          errorMessage = `Incorrect password.`;
        } else {
          errorMessage = error.message;
        }
        break;
      case 'InvalidParameterException':
        try {
          nestedErrorMessage = error.message.toLowerCase();
        } catch (e) {
          errorMessage = DEFAULT_ERROR;
        }
        if (nestedErrorMessage.includes('validation error')) {
          errorMessage =
            'Input error, please remove space and uncommon special characters. ' +
            'Ensure length is less than or equal to 256 characters';
        } else {
          errorMessage = error.message.replace('_', ' ');
        }
        break;
      case 'CodeMismatchException':
        errorMessage = error.message;
        break;
      case 'IdpUserNotFoundException':
        errorMessage = 'No username found. Try again, or register your account.';
        break;
      case 'LimitExceededException':
        errorMessage = error.message;
        break;
      case 'InvalidPasswordException':
        errorMessage = error.message;
        break;
      case 'AccountRecoveryException':
        errorMessage = 'No username found. Try again, or register your account.';
        break;
      case 'MultipleUsersFoundException':
        errorMessage = `Unable to recover account. Please contact customer support ${this.customerSupportNumber} for assistance.`;
        break;
      case 'CidLookupException':
        try {
          nestedErrorMessage = error.message.toLowerCase();
        } catch (e) {
          errorMessage = DEFAULT_ERROR;
        }
        if (nestedErrorMessage.includes('no cid found')) {
          errorMessage = 'No record found. Please check your input and try again.';
        } else {
          errorMessage = `Unable to recover account. Please contact customer support ${this.customerSupportNumber} for assistance.`;
        }
        break;
      case 'UserLambdaValidationException':
        try {
          nestedException = error.message.split(' - ')[1].replace('.', '');
        } catch (e) {
          nestedException = 'UnknownLambdaException';
        }
        switch (nestedException) {
          case 'JwsException':
            errorMessage = 'Session expired. Please refresh this page.';
            break;
          case 'DuplicateUsernameException':
            errorMessage = 'This username is not available.';
            break;
          default:
            errorMessage = DEFAULT_ERROR;
            break;
        }
        break;
      case 'GoogleRecaptchaException':
        errorMessage = 'Session expired. Please refresh this page.';
        break;
      case 'PasswordResetRequiredException':
        errorMessage = 'Password reset required';
        break;
      default:
        errorMessage = DEFAULT_ERROR;
    }

    return this.matSnackBar.openFromComponent(SnackbarNotificationComponent, {
      duration: 10000,
      verticalPosition: 'top',
      horizontalPosition: 'center',
      panelClass: 'snackbar-notification--error',
      data: {
        notificationType: 'error',
        text: errorMessage,
      },
    });
  }
}
